import get from "lodash/get";
import round from "lodash/round";
import { useTranslation } from "react-i18next";
import { PARAMETER_OPTIONS, RPE_RANGE } from "../../../constants";
import convertToRiR from "../../../utils/converters/convert-intensity";
import { repsValue } from "../../../utils/reps-range";
import ParameterInput from "../../inputs/parameter-input";
import TimeSelector from "../../inputs/time-selector";
import UnitInput from "../../inputs/unit-input-add-exercise";
import { mapParameter } from "../../other-exercise/CustomParametersSets";
import LoadToggler from "./LoadToggler";
import ModTooltip from "./ModTooltip";
import { handleSetEntryKeyDown } from "./set-entry-keyboard";

const { FREEFORM, RPE, RPEAMRAP, LOAD } = PARAMETER_OPTIONS;

const EditSetRow = ({
  defaultRepMax,
  editSet,
  form,
  formSets,
  isAssistant,
  setIndex,
  activeUserToRequest,
  handleEditSetChange,
  handleUnitChange,
  preferredLoadInput,
  setPreferredLoadInput,
  handleUpdateSet,
  primary,
  selectedExercise,
  activeUser,
  complexLength,
  // Add Exercise button flow: Esc cancels the edit and the button reads
  // "Update set". The Complexes tab does not pass it.
  onCancelEdit,
}) => {
  const { t } = useTranslation(["coaching-portal", "common"]);
  const formSet = formSets[setIndex];
  const repMax =
    (form.type !== "primary" ? form.secondaryRepMaximum : defaultRepMax) ||
    defaultRepMax;

  const percentRM = round((formSet.load / parseFloat(repMax)) * 100, 1);
  const intensityMeasure =
    get(activeUserToRequest, "intensityMeasure", "rpe") === "rpe"
      ? "RPE"
      : "RiR";
  const isRiR = intensityMeasure === "RiR";
  const onSetFieldKeyDown = (e) => {
    if (onCancelEdit && e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      onCancelEdit();
      return;
    }
    handleSetEntryKeyDown(e, () => handleUpdateSet(setIndex));
  };
  const updateLabel = onCancelEdit
    ? t("addExerciseModal.updateSet")
    : t("common:save");

  if (primary === "assistant" && selectedExercise?.parameters) {
    return (
      // biome-ignore lint/a11y/noStaticElementInteractions: Enter saves the set from whichever field in the row is focused.
      <div className="add-set-wrapper" onKeyDown={onSetFieldKeyDown}>
        {selectedExercise.parameters.map((elem) => {
          return (
            <div
              key={`edit_set_${setIndex}_${elem}`}
              className={`column${
                elem === FREEFORM ? " free-form-col" : " relative"
              }`}
            >
              <label htmlFor={elem.toLowerCase()}>
                {[RPE, RPEAMRAP].includes(elem) ? intensityMeasure : elem}
                {elem === LOAD && preferredLoadInput === "drop"
                  ? ` ${t("addExerciseModal.modSuffix")}`
                  : ""}
              </label>
              {elem === LOAD && (
                <ModTooltip preferredLoadInput={preferredLoadInput} />
              )}
              <ParameterInput
                parameter={elem}
                value={editSet[mapParameter(elem)]}
                onChange={handleEditSetChange}
                units={activeUser.units}
                tabIndex={"0"}
                id={`${elem}_id_parameer_edit`}
                intensityMeasure={intensityMeasure}
                isRiR={isRiR}
                preferredLoadInput={preferredLoadInput}
                loadDrop={editSet.loadDrop}
              />
            </div>
          );
        })}
        {selectedExercise?.parameters?.includes(LOAD) && (
          <LoadToggler
            isAssistant
            preferredLoadInput={preferredLoadInput}
            setPreferredLoadInput={setPreferredLoadInput}
            tabIndex={-1}
          />
        )}

        <TimeSelector
          onChange={handleEditSetChange}
          restTime={editSet.restTime}
        />

        <button
          className="mt-4"
          data-button-type="primary"
          tabIndex={"0"}
          onClick={() => handleUpdateSet(setIndex)}
          type="button"
        >
          {updateLabel}
        </button>
      </div>
    );
  }

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: Enter saves the set from whichever field in the row is focused.
    <div className="add-set-wrapper" onKeyDown={onSetFieldKeyDown}>
      <div className="column">
        <label htmlFor="sets">{t("addExerciseModal.sets")}</label>
        <input
          autoFocus
          tabIndex={"0"}
          className="text-input"
          type="number"
          name="sets"
          defaultValue={round(formSet.sets)}
          onChange={(e) => handleEditSetChange(e, setIndex)}
        />
      </div>

      {!complexLength ? (
        <div className="column">
          <label htmlFor="reps">{t("addExerciseModal.reps")}</label>
          <input
            tabIndex={"0"}
            className="text-input"
            type="text"
            name="reps"
            defaultValue={repsValue(formSet.reps, formSet.repsLower)}
            onChange={(e) => handleEditSetChange(e, setIndex)}
          />
        </div>
      ) : (
        <div className="column complex-reps">
          <div className="input-group">
            <label htmlFor="reps">{t("addExerciseModal.reps")}</label>
            <input
              tabIndex={"0"}
              className="text-input"
              type="text"
              name="reps"
              defaultValue={repsValue(formSet.reps, formSet.repsLower)}
              onChange={(e) => handleEditSetChange(e, setIndex)}
            />
          </div>

          <div className="input-group">
            <div className="input-divider" />
            <label htmlFor="repsTwo">{t("addExerciseModal.plusReps")}</label>
            <input
              tabIndex={"0"}
              type="text"
              name="repsTwo"
              defaultValue={formSet.repsTwo}
              onChange={(e) => handleEditSetChange(e, setIndex)}
            />
          </div>

          {complexLength > 2 && (
            <div className="input-group">
              <div className="input-divider" />
              <label htmlFor="repsThree">
                {t("addExerciseModal.plusReps")}
              </label>
              <input
                tabIndex={"0"}
                type="text"
                name="repsThree"
                defaultValue={formSet.repsThree}
                onChange={(e) => handleEditSetChange(e, setIndex)}
              />
            </div>
          )}
        </div>
      )}

      <div className="column">
        <label htmlFor="rpe">
          {isRiR ? t("addExerciseModal.rir") : t("addExerciseModal.rpeAmrap")}
        </label>
        <select
          tabIndex={"0"}
          className="select-input"
          name="rpe"
          defaultValue={formSet.rpe}
          onChange={(e) => handleEditSetChange(e, setIndex)}
        >
          <option value="">{intensityMeasure}</option>
          {RPE_RANGE.map((i) => (
            <option key={i} value={i + 1}>
              {convertToRiR(i + 1, !isRiR)}
            </option>
          ))}
          <option value="AMRAP">AMRAP</option>
        </select>
      </div>

      <div className="column relative">
        <label>
          {t("addExerciseModal.load")}
          {preferredLoadInput === "drop"
            ? ` ${t("addExerciseModal.modSuffix")}`
            : ""}
        </label>
        <ModTooltip preferredLoadInput={preferredLoadInput} />
        {isAssistant ? (
          <div className="Load-Selector">
            <div className={preferredLoadInput === "drop" ? "hidden" : ""}>
              <UnitInput
                tabIndex={preferredLoadInput === "drop" ? "-1" : "0"}
                metric="kg"
                imperial="lb"
                id="exerciseEditWeightLoad"
                className="text-input"
                name="load"
                placeholder="Weight"
                defaultValue={formSet.load}
                type="text"
                onProgramEditor={true}
                onInput={(e) => handleEditSetChange(e, setIndex)}
                units={editSet.units["load"]}
                handleUnitChange={(e) => handleUnitChange(e, true)}
                unitsTabIndex={-1}
                min={0}
              />
            </div>
            <div
              className={preferredLoadInput === "drop" ? "relative" : "hidden"}
            >
              <input
                tabIndex={preferredLoadInput === "drop" ? "0" : "-1"}
                type="text"
                name="loadDrop"
                id="exerciseEditLoadDrop"
                defaultValue={formSet.loadDrop}
                placeholder="%"
                onInput={(e) => handleEditSetChange(e, setIndex)}
              />
              <span className="load-suffix">
                {t("addExerciseModal.percentModSuffix")}
              </span>
            </div>
          </div>
        ) : (
          <div className="Load-Selector-2">
            <div
              className={
                preferredLoadInput === "percent" ? "relative" : "hidden"
              }
            >
              <input
                tabIndex={preferredLoadInput === "percent" ? "0" : "-1"}
                type="text"
                min={0}
                id="exerciseEditLoad"
                name="percentRepMax"
                className="text-input"
                defaultValue={percentRM}
                placeholder="%"
                onChange={(e) => handleEditSetChange(e, setIndex)}
              />
              <span className="load-suffix">%</span>
            </div>
            <div className={preferredLoadInput === "weight" ? "" : "hidden"}>
              <UnitInput
                tabIndex={preferredLoadInput === "weight" ? "0" : "-1"}
                metric="kg"
                imperial="lb"
                className="text-input"
                name="load"
                placeholder="Weight"
                defaultValue={formSet.load}
                id="exerciseEditWeightLoad"
                type="text"
                onProgramEditor={true}
                onInput={(e) => handleEditSetChange(e, setIndex)}
                units={editSet.units.load}
                handleUnitChange={(e) => handleUnitChange(e, true)}
                unitsTabIndex={-1}
                min={0}
              />
            </div>
            <div
              className={preferredLoadInput === "drop" ? "relative" : "hidden"}
            >
              <input
                tabIndex={preferredLoadInput === "drop" ? "0" : "-1"}
                type="text"
                name="loadDrop"
                id="exerciseEditLoadDrop"
                defaultValue={formSet.loadDrop}
                placeholder="%"
                onInput={(e) => handleEditSetChange(e, setIndex)}
              />
              <span className="load-suffix">
                {t("addExerciseModal.percentModSuffix")}
              </span>
            </div>
          </div>
        )}
      </div>

      <LoadToggler
        isAssistant={isAssistant}
        preferredLoadInput={preferredLoadInput}
        setPreferredLoadInput={setPreferredLoadInput}
        tabIndex={-1}
      />

      <TimeSelector
        onChange={(e) => handleEditSetChange(e, setIndex)}
        restTime={formSet.restTime}
      />

      <button
        className="mt-4"
        data-button-type="primary"
        tabIndex={"0"}
        onClick={(_) => handleUpdateSet(setIndex)}
        type="button"
      >
        {updateLabel}
      </button>
    </div>
  );
};

export default EditSetRow;
